import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Platform,
  View,
  Text,
  TouchableOpacity,
  Image,
} from 'react-native';
import ProductsService from '../../services/ProductsService/ProductsService';
import { useBagStore } from '../../store/BagStore';
import { useUserStore } from '../../store/UserStore';
import { useCatalogStore } from '../../store/CatalogStore';
import ProductList from '../Home/components/ProductList/ProductList';
import { styles } from './styles';
import { calculatePriceBasedOnQuantity } from '../../utils/price';
import BackToTopButton from '../../components/BackToTopButton/BackToTopButton';
import { Dimensions } from 'react-native';
import NotFound from '../Menu/components/NotFound/NotFound';
import { AppRoutes } from '../../navigation/Routes.types';
import { Colors } from '../../constants/Colors';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import AxiosService from '../../services/AxiosService';
import { showInfo } from '../../utils/globalAlert';

interface IFilterParams {
  perPage: number;
  currentPage: number;
}

const FeaturedProducts = () => {
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [featuredData, setFeaturedData] = useState<IProduct[]>([]);
  const { user } = useUserStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [favoriteProducts, setFavoriteProducts] = useState<any[]>([]);
  const navigation = useNavigation();

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    currentPage: 1,
    perPage: 30,
  });

  const { addProduct, updateProductQuantity } = useBagStore();
  const products = useBagStore(state => state.products);

  const flatListRef = useRef<FlatList>(null);
  const [showButton, setShowButton] = useState(false);

  const handleScroll = (event: any) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;
    const deviceHeight = Dimensions.get('window').height;
    const isScrollable = contentSize.height > deviceHeight * 1.25;
    setShowButton(isScrollable && contentOffset.y > deviceHeight);
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showButton) {
      interval = setInterval(() => {
        setShowButton(false);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [showButton]);

  const scrollToTop = () => {
    flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
  };

  const FeaturedProductsGet = useCallback(
    async (
      clientCode: string | undefined,
      perPage: number,
      currentPage: number,
    ) => {
      setLoading(true);

      try {
        const response = await ProductsService.featuredProductsGet(
          clientCode,
          perPage,
          currentPage,
        );

        console.log('Featured Products Response:', response);

        let featuredProducts = [];
        
        if (response.data && Array.isArray(response.data)) {
          featuredProducts = response.data.filter((item: any) => {
            // Filtra apenas produtos válidos com estoque
            return item.sku && item.estoque > 0;
          });
        }

        console.log('Processed Featured Products:', featuredProducts);

        // Sincroniza quantidades com produtos no carrinho
        if (featuredProducts.length > 0) {
          products.forEach(product => {
            const productInCatalog = featuredProducts.find(
              (item: any) => item.sku === product.sku,
            );
            if (productInCatalog) {
              productInCatalog.quantity = product.quantity;
              const { price, discountedPrice } = calculatePriceBasedOnQuantity(
                productInCatalog,
                product.quantity,
              );
              productInCatalog.preco = price;
              productInCatalog.discountedPrice = discountedPrice;
            }
          });
        }

        setFeaturedData(featuredProducts);
      } catch (error) {
        console.error('Error fetching featured products:', error);
        setFeaturedData([]);
      }
      setLoading(false);
    },
    [setLoading, products],
  );

  const sendCart = async (products: any[]) => {
    const instance = AxiosService.createProductsAxiosInstance();
    const data = products.map(product => ({
      sku: product.sku,
      quantity: product.quantity,
    }));
    const response = await instance.post(
      '/carts',
      {
        client_code: user?.clientCode,
        data,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    if (!response.data.data) {
      console.error('sendCart: No data received from server');
      throw new Error('No data received from server');
    }
  };

  const handleAddBag = (product: IProduct, quantity: number) => {
    const emptyBag = products.length === 0;
    const cartKey = (product as any).is_promotion ? `${product.sku}_promo` : product.sku;

    const {products: currentProducts} = useBagStore.getState();
    if (currentProducts.find(prod => (prod.cartKey ?? prod.sku) === cartKey)) {
      updateProductQuantity(cartKey, quantity);
      return;
    } else if (quantity > 0) {
      addProduct({
        ...product,
        quantity: quantity,
        cartKey,
      });
    } else {
      showInfo('Conecta Brasal', 'Informe a quantidade de itens.');
    }

    if (emptyBag) {
      try {
        sendCart(products);
      } catch (error) {
        console.error('Erro ao enviar o carrinho:', error);
      }
    }
  };

  useEffect(() => {
    if (user?.clientCode && user?.storeAccess) {
      FeaturedProductsGet(
        user?.clientCode,
        filterParams.perPage,
        filterParams.currentPage,
      );
    }
  }, [user?.clientCode, user?.storeAccess, filterParams]);

  const renderItem = ({ item }: { item: IProduct }) => (
    <ProductList
      clientCode={user?.clientCode}
      favoriteProducts={favoriteProducts}
      item={item}
      image={item.image}
      title={item.custom_title || item.descricao}
      description={
        (item.custom_title || item.descricao) + ' ' + ((item.complemento || '').replace('- R$', ''))
      }
      AddBag={(quantity: number) => handleAddBag(item, quantity)}
      onPress={() => openProduct(item)}
    />
  );

  const openProduct = (product: IProduct) => {
    navigation.navigate(AppRoutes.PRODUCT_DETAILS, { item: product });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setLoading(true);
    try {
      if (user?.clientCode && user?.storeAccess) {
        setFeaturedData([]);
        setFilterParams({
          currentPage: 1,
          perPage: 30,
        });
      }
    } catch (error) {
      console.error('Erro ao recarregar dados:', error);
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 2000);
    }
  };

  if (!user) return <></>;

  const renderHeader = () => (
    <>
      {/* Banner Header */}
      <View style={{
        width: '100%',
        height: 180,
        backgroundColor: Colors.RedPallete.red,
        position: 'relative',
      }}>
        {/* Botão Voltar */}
        <TouchableOpacity 
          onPress={() => navigation.goBack()}
          style={{
            position: 'absolute',
            top: 50,
            left: 18,
            zIndex: 10,
          }}
        >
          <Text style={{
            fontSize: 14, 
            color: '#FFF', 
            fontFamily: 'TCCC-UnityText'
          }}>
            ← Voltar
          </Text>
        </TouchableOpacity>

        {/* Banner Content */}
        <View style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: 40,
        }}>
          <Text style={{
            fontSize: 32,
            fontFamily: 'TCCC-UnityText Bold',
            color: '#FFF',
            textAlign: 'center',
            lineHeight: 36,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            PRODUTOS{'\n'}IMPERDÍVEIS!
          </Text>
          <Text style={{
            fontSize: 14,
            fontFamily: 'TCCC-UnityText',
            color: '#FFD700',
            textAlign: 'center',
            marginTop: 8,
            textTransform: 'uppercase',
          }}>
            Escolhidos para você!
          </Text>
        </View>
      </View>

     
    </>
  );

  return (
    <View style={styles.container}>
      <FlatList
        ref={flatListRef}
        data={featuredData}
        renderItem={renderItem}
        showsVerticalScrollIndicator={false}
        keyExtractor={(item, index) => 'featured-' + index}
        numColumns={2}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={{
          paddingBottom: Platform.OS === 'ios' ? 105 : 105, // Espaço para o tab bar (85) + margem extra (20)
          flexGrow: 1,
        }}
        columnWrapperStyle={{
          paddingHorizontal: 10,
        }}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        ListEmptyComponent={
          loading ? (
            <></>
          ) : (
            <View style={{ marginVertical: 50 }}>
              <NotFound
                description="Nenhum produto em destaque encontrado."
                title="Ops..."
              />
            </View>
          )
        }
        ListFooterComponent={loading ? <SkeletonLoader /> : <></>}
        onEndReached={() => {
          if (
            !loading &&
            featuredData &&
            featuredData.length > 0 &&
            featuredData.length === filterParams.perPage
          ) {
            setFilterParams({
              ...filterParams,
              perPage: filterParams.perPage + 30,
            });
          }
        }}
        onEndReachedThreshold={0.1}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      />
      
      <BackToTopButton onPress={scrollToTop} visible={showButton} />
    </View>
  );
};

export default FeaturedProducts;
